import { action, computed, makeAutoObservable, observable } from "mobx";
import Store from "./Store";

export type ProfileForm = {
  fullName: string;
  sex: string;
  birthDate: string;
  weight: number | null;
  city: string;
  // about: string;
  password?: string | undefined;
  passwordConfirm?: string | undefined;
};

export default class ProfileStore {
  isProfileOpen: boolean = false;
  isPaymentOpen: boolean = false;
  activeSubscriptions: string[] = [];
  isCoach: boolean = false;
  profileFile: string | null = null;
  // When true, shows the avatar editing modal
  isAvatarEditorOpen: boolean = false;
  // Original file selected by the user before editing
  originalAvatarFile: File | null = null;
  // Cropped image to upload
  profileFileToUpload: Blob | File | null = null;
  // Defer avatar cleanup action until profile modal fully closed
  avatarCleanupOnClose: "discard" | "finalize" | null = null;
  // Cache-busting version for GET /users/{id}/avatar
  avatarVersion: number = 0;

  profileFormData: ProfileForm = {
    fullName: "",
    city: "",
    sex: "male",
    weight: null,
    birthDate: "",
    // about: "",
    passwordConfirm: undefined,
    password: undefined,
  };

  constructor(private store: Store) {
    makeAutoObservable(this, {
      setIsProfileOpen: action.bound,
      isProfileOpen: observable,
      isPaymentOpen: observable,
      setIsPaymentOpen: action.bound,
      activeSubscriptions: observable,
      hasCoachSubscription: computed,
      hasAiSubscription: computed,
      isCoach: observable,
      setIsCoach: action.bound,
      profileFormData: observable,
      isAvatarEditorOpen: observable,
      originalAvatarFile: observable,
      profileFileToUpload: observable,
      avatarCleanupOnClose: observable,
      avatarVersion: observable,
      setActiveSubscriptions: action.bound,
      setFormData: action.bound,
      getFormData: action.bound,
      setPassword: action.bound,
      setPasswordConfirm: action.bound,
      setAvatarEditorOpen: action.bound,
      setOriginalAvatarFile: action.bound,
      setProfileFileToUpload: action.bound,
      setCurrentFile: action.bound,
      resetAvatarEditing: action.bound,
      scheduleAvatarCleanup: action.bound,
      applyAvatarCleanupOnClose: action.bound,
      bumpAvatarVersion: action.bound,
    });
  }
  setIsProfileOpen(isProfileOpen: boolean) {
    this.isProfileOpen = isProfileOpen;
  }

  setIsPaymentOpen(isPaymentOpen: boolean) {
    this.isPaymentOpen = isPaymentOpen;
  }

  get hasCoachSubscription(): boolean {
    return this.activeSubscriptions.includes("coach");
  }

  get hasAiSubscription(): boolean {
    return this.activeSubscriptions.includes("ai");
  }

  setActiveSubscriptions(subscriptions: string[]) {
    this.activeSubscriptions = subscriptions ?? [];
  }

  setIsCoach(isCoach: boolean) {
    this.isCoach = isCoach;
  }

  setCurrentFile(file: any) {
    this.profileFile = file;
  }

  setAvatarEditorOpen(isOpen: boolean) {
    this.isAvatarEditorOpen = isOpen;
  }

  setOriginalAvatarFile(file: File | null) {
    this.originalAvatarFile = file;
  }

  setProfileFileToUpload(file: Blob | File | null) {
    this.profileFileToUpload = file;
  }

  resetAvatarEditing() {
    this.profileFile = null;
    this.profileFileToUpload = null;
    this.originalAvatarFile = null;
    this.isAvatarEditorOpen = false;
  }

  scheduleAvatarCleanup(mode: "discard" | "finalize") {
    this.avatarCleanupOnClose = mode;
  }

  applyAvatarCleanupOnClose() {
    if (this.avatarCleanupOnClose === "discard") {
      this.resetAvatarEditing();
    } else if (this.avatarCleanupOnClose === "finalize") {
      // Keep current preview, just clear transient editing artifacts
      this.profileFileToUpload = null;
      this.originalAvatarFile = null;
      this.isAvatarEditorOpen = false;
    }
    this.avatarCleanupOnClose = null;
  }

  bumpAvatarVersion() {
    this.avatarVersion = Date.now();
  }

  setFormData(key: string, value: any) {
    //@ts-ignore
    this.profileFormData[key] = value;
  }

  getFormData(key: string): any {
    //@ts-ignore
    return this.profileFormData[key];
  }

  setAllFormData(formData: ProfileForm | null | undefined) {
    if (formData == null) return;
    this.profileFormData = {
      ...this.profileFormData,
      ...formData,
      password: undefined,
      passwordConfirm: undefined,
    };
  }

  setFormDataByKey(formData: Partial<ProfileForm>) {
    if (!formData) return;
    Object.entries(formData).forEach(([key, value]) => {
      if (key in this.profileFormData) {
        //@ts-ignore
        this.profileFormData[key] = value;
      }
    });
  }

  setPassword(password: string) {
    this.profileFormData.password = password;
  }

  setPasswordConfirm(password: string) {
    this.profileFormData.passwordConfirm = password;
  }
}
